import { useEffect, useState } from "react";
import { ListBase, useListContext } from "ra-core";
import { Search, Users } from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router";

import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { usePermission } from "../providers/usePermission";
import { ContactCreateDialog } from "./ContactCreateDialog";
import { EmptyState, GraphError, GraphLoading } from "./GraphStatus";
import { ListPage, RowAvatar } from "./record/ListPage";
import { humanize, RECORD_LINK } from "./record/styles";
import { SignalBadges } from "./SignalBadge";
import { Input } from "@/components/ui/input";

interface GraphContact {
  id: string;
  signal_types?: string[];
  first_name: string;
  last_name: string;
  primary_type?: string | null;
  company_id?: string | null;
  company_name?: string | null;
  email?: string | null;
  phone?: string | null;
}

export function GraphContactList() {
  const [params] = useSearchParams();
  const q = params.get("q") ?? undefined;
  return (
    <ListBase
      perPage={200}
      sort={{ field: "last_name", order: "ASC" }}
      filter={q ? { q } : undefined}
    >
      <ContactRows />
    </ListBase>
  );
}

function ContactRows() {
  const { data, total, isPending, error } = useListContext<GraphContact>();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") ?? "");
  const canCreate = usePermission("create_records");
  const navigate = useNavigate();

  useEffect(() => {
    const handle = window.setTimeout(() => {
      const next = new URLSearchParams(params);
      const trimmed = q.trim();
      if (trimmed) next.set("q", trimmed);
      else next.delete("q");
      if (next.toString() !== params.toString()) setParams(next);
    }, 200);
    return () => window.clearTimeout(handle);
  }, [q, params, setParams]);

  if (isPending && !data) return <GraphLoading />;
  if (error) return <GraphError error={error} noun="contact list" />;

  const rows = data ?? [];
  return (
    <ListPage
      title="Contacts"
      count={total ?? rows.length}
      actions={canCreate ? <ContactCreateDialog /> : null}
      toolbar={
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
          <Input
            value={q}
            onChange={(event) => setQ(event.target.value)}
            placeholder="Search name or NMLS"
            className="w-72 bg-background pl-8"
            aria-label="Search name or NMLS"
          />
        </div>
      }
      footer={
        total != null && total > rows.length
          ? `Showing the first ${rows.length} of ${total}. Search to narrow the list.`
          : null
      }
    >
      <Table>
        <TableHeader className="bg-muted/40">
          <TableRow>
            <TableHead className="pl-4">Name</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Company</TableHead>
            <TableHead className="hidden lg:table-cell">Email</TableHead>
            <TableHead className="hidden lg:table-cell">Phone</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} className="p-0">
                <EmptyState icon={Users} title="No contacts match.">
                  Try another name or NMLS number.
                </EmptyState>
              </TableCell>
            </TableRow>
          ) : null}
          {rows.map((row) => {
            const name =
              `${row.first_name ?? ""} ${row.last_name ?? ""}`.trim();
            return (
              <TableRow
                key={row.id}
                className="cursor-pointer"
                onClick={(event) => {
                  if ((event.target as HTMLElement).closest("a")) return;
                  navigate(`/contacts/${row.id}/show`);
                }}
              >
                <TableCell className="pl-4">
                  <div className="flex items-center gap-3">
                    <RowAvatar name={name} />
                    <Link
                      className={RECORD_LINK}
                      to={`/contacts/${row.id}/show`}
                    >
                      {name}
                    </Link>
                    <SignalBadges types={row.signal_types} />
                  </div>
                </TableCell>
                <TableCell>
                  {row.primary_type ? (
                    <Badge variant="secondary" className="font-normal">
                      {humanize(row.primary_type)}
                    </Badge>
                  ) : null}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {row.company_id ? (
                    <Link
                      className={
                        RECORD_LINK + " font-normal text-muted-foreground"
                      }
                      to={`/companies/${row.company_id}/show`}
                    >
                      {row.company_name}
                    </Link>
                  ) : null}
                </TableCell>
                <TableCell className="hidden text-muted-foreground lg:table-cell">
                  {row.email}
                </TableCell>
                <TableCell className="hidden text-muted-foreground lg:table-cell">
                  {row.phone}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </ListPage>
  );
}
